import { useEffect } from "react";
import { useRouter } from "next/router";
import { OrderStatus } from "~graphql/sdk";

interface UseUnsavedChangesWarningParams {
  isRenewMembershipsOrder: boolean;
  orderStatus?: OrderStatus;
  setShowLeaveModal: (show: boolean) => void;
}

/**
 * Custom hook to handle unsaved changes warnings and browser navigation
 * for checkout flows. Sets window.onbeforeunload for non-renewal orders
 * and router.beforePopState to show confirmation modal.
 */
export const useUnsavedChangesWarning = ({
  isRenewMembershipsOrder,
  orderStatus,
  setShowLeaveModal,
}: UseUnsavedChangesWarningParams) => {
  const router = useRouter();

  useEffect(() => {
    // Don't set warnings for completed orders
    if (
      orderStatus === OrderStatus.Paid ||
      orderStatus === OrderStatus.Completed
    ) {
      return;
    }

    // Set beforeunload warning for non-renewal orders
    if (!isRenewMembershipsOrder) {
      const handleBeforeUnload = () => true;
      window.onbeforeunload = handleBeforeUnload;
    }

    // Set browser back button handler to show confirmation modal
    const handleBeforePopState = ({ as }: { as: string }) => {
      if (as !== router.asPath) {
        setShowLeaveModal(true);
        return false; // Prevent navigation
      }
      return true;
    };

    router.beforePopState(handleBeforePopState);

    return () => {
      window.onbeforeunload = null;
      router.beforePopState(() => true);
    };
  }, [isRenewMembershipsOrder, orderStatus, router, setShowLeaveModal]);
};
